import React, { useEffect, useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { FaUserCircle, FaBuilding } from "react-icons/fa";
import { FiLoader, FiX } from "react-icons/fi";

const PopupAssign = ({ studentId, onClose }) => {
  const [student, setStudent] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [roomStatus, setRoomStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  /* ================= Fetch Data ================= */
  useEffect(() => {
    if (!studentId) return;

    const fetchData = async () => {
      try {
        const [personalRes, roomRes] = await Promise.all([
          axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net//api/UserInformation/personal/${studentId}`),
          axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/RoomStatus/${studentId}`)
        ]);

        const personal = personalRes.data;
        setStudent(personal);

        if (personal.profilePictureUrl) {
          setProfilePicture(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${personal.profilePictureUrl}`);
        }

        setRoomStatus(roomRes.data?.isAssigned === 1 ? roomRes.data.room : null);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load student data");
      }
    };

    fetchData();
  }, [studentId]);

  /* ================= Actions ================= */
  const handleUnassign = async () => {
    if (!studentId) return;

    setLoading(true);
    try {
      await axios.post(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/RoomStatus/unassign/${studentId}`);
      setRoomStatus(null);
      setShowConfirm(false);
      toast.success("Student removed from room");
       // ✅ Refresh the page after a short delay so the toast is visible
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove student from room");
    } finally {
      setLoading(false);
    }
  };

  /* ================= Loading ================= */
  if (!student) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-6 py-5 flex items-center gap-3">
          <FiLoader className="animate-spin text-gray-600" />
          <span className="text-sm text-gray-700">Loading student details…</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />

      {/* ================= Overlay ================= */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">

        {/* ================= Card ================= */}
        <div className="relative w-full max-w-md bg-white rounded-lg border border-gray-200 shadow-xl">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-800">
              Student Assignment
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-5">

            {/* Student */}
            <div className="flex items-center gap-4">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt="Profile"
                  className="w-14 h-14 rounded-full object-cover border"
                />
              ) : (
                <FaUserCircle className="w-14 h-14 text-gray-300" />
              )}

              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {student.firstName} {student.lastName}
                </p>
                <p className="text-xs text-gray-500">{student.email}</p>
              </div>
            </div>

            {/* Room Info */}
            {roomStatus ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 font-medium text-gray-700 mb-1">
                  <FaBuilding />
                  Room Details
                </div>

                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Room:</span>{" "}
                  {roomStatus.roomNumber}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Building:</span>{" "}
                  {roomStatus.building}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Floor:</span>{" "}
                  {roomStatus.floor}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Capacity:</span>{" "}
                  {roomStatus.capacity}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium text-gray-700">Occupied:</span>{" "}
                  {roomStatus.occupied}
                </p>

                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={loading}
                  className="mt-3 w-full px-3 py-1.5 text-xs rounded-md bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50"
                >
                  Remove from Room
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center">
                Student is not assigned to any room.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ================= Confirm Modal ================= */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-xl max-w-sm w-full p-5 text-center space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Confirm Removal
            </h3>
            <p className="text-sm text-gray-600">
              Remove <strong>{student.firstName} {student.lastName}</strong> from
              the room?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={handleUnassign}
                disabled={loading}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? (
                  <FiLoader className="animate-spin mx-auto" />
                ) : (
                  "Confirm"
                )}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PopupAssign;
